import Stats from '../../api/Stats';

const LIVE_MS = 30 * 1000;
const INSIGHTS_MS = 5 * 60 * 1000;
const MINE_MS = 2 * 60 * 1000;

const empty = () => ({
  scope: 'global',
  overview: null, co2Trend: null, byCountry: null, byTreeType: null, topForests: null, tokenFlow: null,
  customersTrend: null, live: null, liveAt: null, insights: null,
});

/**
 * Loads what the Statistics cards show, into `stats`:
 *   startGlobal() - the whole platform (administrators): public /stats/* figures + /stats/insights, kept fresh
 *   startMine()   - only the signed-in user's own forests and wallet (/stats/me)
 */
export default {
  data() {
    return { stats: empty(), lastUpdated: null, refreshing: false, statsTimers: [] };
  },
  beforeDestroy() {
    this.statsTimers.forEach(clearInterval);
  },
  methods: {
    startGlobal() {
      this.statsMode = 'global';
      this.loadGlobal();
      this.statsTimers.push(setInterval(this.loadLive, LIVE_MS), setInterval(this.loadInsights, INSIGHTS_MS));
    },
    startMine() {
      this.statsMode = 'mine';
      this.loadMine();
      this.statsTimers.push(setInterval(this.loadMine, MINE_MS));
    },
    async refreshStats() {
      this.refreshing = true;
      try { await (this.statsMode === 'mine' ? this.loadMine() : this.loadGlobal()); } finally { this.refreshing = false; }
    },
    async loadGlobal() {
      const take = (p) => p.then((r) => r.data).catch(() => null);
      const [overview, co2Trend, byCountry, byTreeType, topForests, tokenFlow, customersTrend] = await Promise.all([
        take(Stats.getOverview()), take(Stats.getCo2Trend()), take(Stats.getByCountry()), take(Stats.getByTreeType()),
        take(Stats.getTopForests(12)), take(Stats.getTokenFlow()), take(Stats.getCustomersTrend()),
      ]);
      Object.assign(this.stats, {
        scope: 'global',
        overview: overview || {},
        co2Trend: Array.isArray(co2Trend) ? co2Trend : [],
        byCountry: (Array.isArray(byCountry) ? byCountry : []).filter((c) => c.country),
        byTreeType: Array.isArray(byTreeType) ? byTreeType : [],
        topForests: Array.isArray(topForests) ? topForests : [],
        tokenFlow: tokenFlow || {},
        customersTrend: Array.isArray(customersTrend) ? customersTrend : [],
      });
      this.lastUpdated = new Date();
      await Promise.all([this.loadLive(), this.loadInsights()]);
    },
    async loadLive() {
      try {
        const { data } = await Stats.getLive();
        this.stats.live = data || {};
        this.stats.liveAt = new Date();
        this.lastUpdated = new Date();
      } catch (e) {
        if (!this.stats.live) this.stats.live = {};
      }
    },
    async loadInsights() {
      try {
        const { data } = await Stats.getInsights();
        if (data) this.stats.insights = data;
      } catch (e) {
        if (!this.stats.insights) this.stats.insights = { totals: null };
      }
    },
    async loadMine() {
      try {
        const { data } = await Stats.getMine();
        if (data) Object.assign(this.stats, { ...data, scope: 'user', co2Trend: [], customersTrend: null, liveAt: new Date() });
        this.lastUpdated = new Date();
      } catch (e) {
        if (!this.stats.insights) Object.assign(this.stats, { scope: 'user', overview: {}, insights: { totals: null }, live: {}, tokenFlow: {}, byCountry: [], byTreeType: [], topForests: [] });
      }
    },
  },
};
